import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CatTable } from '@blocks-react/components/Table';
import { CatTableObject } from '@blocks-react/components/TableObject';
import ConfirmationModal from '@app/components/common/Blocks/ConfirmationModal/ConfirmationModal';
import Toast from '../../common/Blocks/Toast';
import SavedListTableHeader from './SavedListTableHeader';
import SavedListTableBody from './SavedListTableBody';
import ShareListDrawer from '@app/components/pages/account/saved-list/ShareListDrawer';
import ShareListDrawerTableContainer from '../ShareListDrawer/ShareListDrawerTableContainer';
import ShareListTableHeader from '../ShareListDrawer/ShareListTableHeader';
import ShareListTableBody from '../ShareListDrawer/ShareListTableBody';
import ShareListUCIDDropdown from '../ShareListDrawer/ShareListUCIDDropdown';
import { getOrder } from '@app/store/orders/actions';
import { shareSelectedList } from '@app/store/dealer-customer/actions';
import {
  addToCartDetails,
  deleteSavedListDetails
} from '@app/store/account/actions';
import { replaceTokensInString, isEmpty } from '@app/utils';
import { DEALER } from '@app/constants/affiliations';
import {
  MODAL_ACTIONS,
  SAVED_LIST_COLUMN_IDS,
  getSavedListTableSchema
} from '../constants';
import { DEALER_CUSTOMER_SHARE_UCID_LIST_STATUS_IDLE } from '@app/store/dealer-customer/constants';
import { STATUS, TOAST_VARIANT } from '@app/constants/commonConstants';
import { SOURCE_OF_SUPPLY_API_INITIAL_TIME } from '@app/constants/analyticsConstants';
import { setTimeSessionStorage } from '@app/components/common/analytics/analyticsUtils';
import { commonItemPropType } from '../declarations.propTypes';
import WrapperContainer from '@app/components/common/WrapperContainer';

const SavedListTable = ({ list, handleSort, onPageChange }) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const isCSPCustomer = useSelector(state => state?.common?.isCSPCustomer);
  const isInstantAccess = useSelector(state => state?.common?.isInstantAccess);
  const savedLists = useSelector(s => s.account.savedLists);
  const userAffiliation = useSelector(s => s.common?.userAffiliation);
  const shareListStatus = useSelector(s => s.dealerCustomer?.shareListStatus);
  const isCatCSR = useSelector(s => s.common?.isCatCSR) || false;
  const [toastOptions, setToastOptions] = useState({
    variant: TOAST_VARIANT.NONE,
    message: ''
  });
  const [shareListItem, setShareListItem] = useState({});
  const [selectedList, setSelectedList] = useState([]);
  const [isDrawerActive, setIsDrawerActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isDealerUser = userAffiliation === DEALER;
  const selectedUCIDs = selectedList.map(
    ({ customerOrganizationIdentifier }) => customerOrganizationIdentifier
  );

  const onShareListClick = item => {
    setShareListItem(item);
    setIsDrawerActive(true);
  };
  const addItemToSelectedList = item => {
    setSelectedList(list => [
      ...list,
      ...(!!list.find(
        listItem =>
          listItem.customerOrganizationIdentifier ===
          item.customerOrganizationIdentifier
      )
        ? []
        : [item])
    ]);
    setSearchTerm('');
  };
  const removeItemfromSelectedList = itemId => {
    setSelectedList(list =>
      list.filter(item => item.customerOrganizationIdentifier !== itemId)
    );
  };

  const isSelectedListEmpty = isEmpty(selectedUCIDs);

  const confirmationModalInitState = {
    isOpen: false,
    action: null,
    item: null
  };
  const [confirmationModalState, setConfirmationModalState] = useState(
    confirmationModalInitState
  );
  const resetConfirmationModal = () =>
    setConfirmationModalState(confirmationModalInitState);

  const clearToastMessage = () =>
    setToastOptions({
      variant: TOAST_VARIANT.NONE,
      message: ''
    });

  const setToastMessage = (variant, message) =>
    setToastOptions({
      variant,
      message
    });

  const filteredSchema = !isCSPCustomer
    ? getSavedListTableSchema(isInstantAccess, isCSPCustomer, isCatCSR).filter(
        item => item.id !== SAVED_LIST_COLUMN_IDS.LIST_TYPE
      )
    : getSavedListTableSchema(isInstantAccess, isCSPCustomer, isCatCSR);

  const { variant, message } = toastOptions;
  const { name, orderId } = shareListItem;

  const addToCart = async item => {
    setTimeSessionStorage(SOURCE_OF_SUPPLY_API_INITIAL_TIME);
    resetConfirmationModal();
    await dispatch(getOrder(item.orderId));
    dispatch(addToCartDetails(item.orderId));
  };

  const deleteSavedList = async item => {
    clearToastMessage();
    resetConfirmationModal();
    const res = await dispatch(deleteSavedListDetails(item.orderId));
    if (res) {
      setToastMessage(
        TOAST_VARIANT.SUCCESS,
        replaceTokensInString(t('DELETE_LIST_SUCCESSFULLY'), item.name)
      );
    } else {
      setToastMessage(
        TOAST_VARIANT.ERROR,
        replaceTokensInString(t('DELETE_LIST_CANNOT_BE_DELETED'), item.name)
      );
    }
  };

  const shareList = async () => {
    clearToastMessage();
    resetConfirmationModal();
    setSearchTerm('');
    await dispatch(
      shareSelectedList({
        ucidList: selectedUCIDs,
        requisitionListId: orderId
      })
    );
  };

  const {
    isOpen: isConfModalOpen,
    action: confModalAction,
    item: confModalItem
  } = confirmationModalState;

  const modalOptions = {
    [MODAL_ACTIONS.ADD_TO_CART]: {
      confModalHeading: replaceTokensInString(
        t('ADD_TO_CART_MODAL_HEADER'),
        confModalItem?.quantity
      ),
      confModalMessage: replaceTokensInString(
        t('ADD_TO_CART_MODAL_BODY'),
        confModalItem?.quantity
      ),
      onConfirm: () => addToCart(confModalItem),
      confModalButtonOptions: { primaryButtonVariant: 'primary' }
    },
    [MODAL_ACTIONS.DELETE]: {
      confModalHeading: t('DELETE_THIS_LIST'),
      confModalMessage: t('QUESTION_TO_DELETE_THIS_LIST'),
      onConfirm: () => deleteSavedList(confModalItem),
      confModalButtonOptions: { primaryButtonVariant: 'danger' }
    },
    [MODAL_ACTIONS.SHARE]: {
      confModalHeading: replaceTokensInString(
        t('SHARE_LIST_CONFIRMATION_TITLE'),
        name
      ),
      confModalMessage: replaceTokensInString(
        t('SHARE_SAVEDLIST_ACCOUNT'),
        name,
        selectedList.length
      ),
      onConfirm: shareList,
      confModalButtonOptions: { primaryButtonVariant: 'primary' }
    },
    [MODAL_ACTIONS.CANCEL_SHARE]: {
      confModalHeading: replaceTokensInString(t('CANCEL_SHARING'), name),
      confModalMessage: replaceTokensInString(
        t('SHARE_LIST_CONFIRMATION_BODY'),
        name
      ),
      onConfirm: () => {
        resetConfirmationModal();
        setSelectedList([]);
        setIsDrawerActive(false);
        setSearchTerm('');
        dispatch({
          type: DEALER_CUSTOMER_SHARE_UCID_LIST_STATUS_IDLE,
          payload: STATUS.IDLE
        });
      },
      confModalButtonOptions: { primaryButtonVariant: 'danger' }
    }
  };

  const {
    confModalHeading,
    confModalMessage,
    onConfirm,
    confModalButtonOptions = {}
  } = modalOptions[confModalAction] || {};

  useEffect(() => {
    if (shareListStatus === STATUS.RESOLVED) {
      setToastMessage(TOAST_VARIANT.SUCCESS, t('SHARE_LIST_SUCCESS'));
      setIsDrawerActive(false);
      setSelectedList([]);
      dispatch({
        type: DEALER_CUSTOMER_SHARE_UCID_LIST_STATUS_IDLE,
        payload: STATUS.IDLE
      });
    }
  }, [dispatch, shareListStatus, t]);

  return (
    <>
      {!!savedLists.length && (
        <CatTableObject height="auto">
          <CatTable>
            <SavedListTableHeader
              schema={filteredSchema}
              handleSort={handleSort}
              onPageChange={onPageChange}
            />
            <SavedListTableBody
              schema={filteredSchema}
              list={list}
              onShareListClick={onShareListClick}
              toastOptions={{ setToastMessage, clearToastMessage }}
              onAddToCartClick={item =>
                setConfirmationModalState({
                  isOpen: true,
                  action: MODAL_ACTIONS.ADD_TO_CART,
                  item
                })
              }
              onDeleteClick={item =>
                setConfirmationModalState({
                  isOpen: true,
                  action: MODAL_ACTIONS.DELETE,
                  item
                })
              }
            />
          </CatTable>
        </CatTableObject>
      )}

      <Toast
        variant={variant}
        message={message}
        clearToastMessage={clearToastMessage}
      />
      {isDealerUser && (
        <WrapperContainer>
          <ShareListDrawer
            isActive={isDrawerActive}
            closeShareListDrawer={() => {
              setIsDrawerActive(false);
              setSearchTerm('');
            }}
            shareListItem={shareListItem}
            isSelectedListEmpty={isSelectedListEmpty}
            setConfirmationModalState={setConfirmationModalState}
            showError={shareListStatus === STATUS.REJECTED}
          >
            <ShareListUCIDDropdown
              addItemToSelectedList={addItemToSelectedList}
              selectedUCIDs={selectedUCIDs}
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
            />
            <ShareListDrawerTableContainer>
              <ShareListTableHeader hideBottomBorder={isSelectedListEmpty} />
              {isSelectedListEmpty && (
                <div className="cat-u-theme-typography-body-sm cat-u-margin-top-lg cat-u-margin-left-md">
                  {t('NO_ACCOUNTS_SELECTED')}
                </div>
              )}
              <ShareListTableBody
                selectedList={selectedList}
                removeItemfromSelectedList={removeItemfromSelectedList}
              />
            </ShareListDrawerTableContainer>
          </ShareListDrawer>
        </WrapperContainer>
      )}
      <ConfirmationModal
        isOpen={isConfModalOpen}
        heading={confModalHeading}
        message={confModalMessage}
        onConfirm={onConfirm}
        onClose={resetConfirmationModal}
        buttonOptions={confModalButtonOptions}
      />
    </>
  );
};
export default SavedListTable;
SavedListTable.propTypes = {
  list: PropTypes.arrayOf(commonItemPropType),
  handleSort: PropTypes.func,
  onPageChange: PropTypes.func
};
